"use client";

import { useEffect, useRef } from "react";

const testimonials = [
  {
    id: "t1",
    name: "Alejandro Martín Reyes",
    role: "Arquitecto",
    company: "Estudio Martín Arquitectura, Las Palmas",
    quote: "Los renders que nos entregó Evolvia3D para el proyecto de la urbanización Maspalomas fueron absolutamente fotorrealistas. El cliente firmó al primer vistazo. Repetiremos sin duda.",
    rating: 5,
    initials: "AM",
  },
  {
    id: "t2",
    name: "Sofía González Hernández",
    role: "Propietaria",
    company: "Tienda Artesanía Tenerife, Santa Cruz",
    quote: "Encargué una colección de figuras decorativas inspiradas en la flora canaria para mi tienda. La calidad del acabado y los detalles son increíbles. Mis clientes los adoran.",
    rating: 5,
    initials: "SG",
  },
  {
    id: "t3",
    name: "Carlos Domínguez Pérez",
    role: "Director de Producto",
    company: "BioTech Canarias, Las Palmas",
    quote: "Necesitábamos prototipos funcionales para nuestra ronda de inversión. Evolvia3D entregó en 8 días con tolerancias de 0.05mm. Profesionalidad total y comunicación excelente.",
    rating: 5,
    initials: "CD",
  },
];

export default function TestimonialsSection() {
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) entry.target.classList.add("visible");
        });
      },
      { threshold: 0.1 }
    );
    const items = ref?.current?.querySelectorAll(".reveal");
    items?.forEach((el) => observer?.observe(el));
    return () => observer?.disconnect();
  }, []);

  return (
    <section ref={ref} className="py-28 px-6 lg:px-10 bg-evolvia-bg relative overflow-hidden">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="text-center mb-16 reveal">
          <span className="text-xs font-700 uppercase tracking-[0.25em] text-evolvia-copper mb-3 block">
            Testimonios
          </span>
          <h2 className="text-5xl md:text-6xl font-black leading-[0.92] tracking-tight text-evolvia-text-primary mb-5">
            Lo que dicen nuestros
            <br />
            <span className="gradient-text-copper">clientes en Canarias.</span>
          </h2>
        </div>

        {/* Trust indicators */}
        <div className="reveal delay-100 flex flex-wrap justify-center gap-8 mb-16">
          {[
            { value: "120+", label: "Proyectos completados" },
            { value: "4.9★", label: "Valoración media" },
            { value: "7 años", label: "De experiencia" },
            { value: "100%", label: "Satisfacción garantizada" },
          ]?.map((stat) => (
            <div key={stat?.label} className="text-center">
              <p className="text-2xl font-black text-evolvia-copper">{stat?.value}</p>
              <p className="text-xs text-evolvia-text-muted uppercase tracking-wider mt-1">{stat?.label}</p>
            </div>
          ))}
        </div>

        {/* Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {testimonials?.map((t, i) => (
            <div
              key={t?.id}
              className={`reveal delay-${i * 200} border border-evolvia-border rounded-sm p-8 flex flex-col gap-6 group hover:border-evolvia-copper transition-all duration-400 bg-evolvia-card`}
            >
              {/* Stars */}
              <div className="flex gap-1">
                {[...Array(t?.rating)]?.map((_, si) => (
                  <svg key={si} className="w-4 h-4 text-evolvia-copper" fill="currentColor" viewBox="0 0 20 20">
                    <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                  </svg>
                ))}
              </div>

              {/* Quote */}
              <blockquote className="text-evolvia-text-secondary text-sm leading-relaxed flex-1">
                &ldquo;{t?.quote}&rdquo;
              </blockquote>

              {/* Author */}
              <div className="flex items-center gap-3 pt-4 border-t border-evolvia-border">
                <div className="w-10 h-10 rounded-sm flex items-center justify-center text-sm font-700 flex-shrink-0 bg-evolvia-copper/15 border border-evolvia-copper/25 text-evolvia-copper">
                  {t?.initials}
                </div>
                <div>
                  <p className="text-sm font-600 text-evolvia-text-primary">{t?.name}</p>
                  <p className="text-xs text-evolvia-text-muted">{t?.role} · {t?.company}</p>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}